import React, { useEffect, useState, useContext } from "react";
import axios from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { motion } from "framer-motion";

export default function ReportsBoth() {
  const { user } = useContext(AuthContext);

  const [incomeYear, setIncomeYear] = useState(0);
  const [prevIncomeYear, setPrevIncomeYear] = useState(0);
  const [expenseYear, setExpenseYear] = useState(0);
  const [prevExpenseYear, setPrevExpenseYear] = useState(0);

  const [incomeMonth, setIncomeMonth] = useState(0);
  const [prevIncomeMonth, setPrevIncomeMonth] = useState(0);
  const [expenseMonth, setExpenseMonth] = useState(0);
  const [prevExpenseMonth, setPrevExpenseMonth] = useState(0);

  const [incomeWeek, setIncomeWeek] = useState(0);
  const [prevIncomeWeek, setPrevIncomeWeek] = useState(0);
  const [expenseWeek, setExpenseWeek] = useState(0);
  const [prevExpenseWeek, setPrevExpenseWeek] = useState(0);

  const [monthlyData, setMonthlyData] = useState([]);
  const [incomeRadarData, setIncomeRadar] = useState([]);
  const [expenseRadarData, setExpenseRadar] = useState([]);

  // only additions:
  const [isMobile, setIsMobile] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeMonth, setActiveMonth] = useState(null);

  const incomeCategories = [
    "Salary",
    "Freelance",
    "Investments",
    "Consulting",
    "Online Sales",
    "Gifts",
  ];
  const expenseCategories = [
    "Food",
    "Transport",
    "Utilities",
    "Shopping",
    "Entertainment",
    "Healthcare",
  ];

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: txns } = await axios.get("/finance/transactions/");

        const now = new Date();
        const thisYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const lastMonthYear = currentMonth === 0 ? thisYear - 1 : thisYear;

        const startOfMonth = new Date(thisYear, currentMonth, 1, 0, 0, 0, 0);
        const startOfNextMonth = new Date(thisYear, currentMonth + 1, 1, 0, 0, 0, 0);
        const startOfLastMonth = new Date(lastMonthYear, lastMonth, 1, 0, 0, 0, 0);

        const isoDay = now.getDay() === 0 ? 7 : now.getDay();
        const startOfThisWeek = new Date(now);
        startOfThisWeek.setDate(now.getDate() - (isoDay - 1));
        startOfThisWeek.setHours(0, 0, 0, 0);

        const startOfNextWeek = new Date(startOfThisWeek);
        startOfNextWeek.setDate(startOfThisWeek.getDate() + 7);

        const startOfLastWeek = new Date(startOfThisWeek);
        startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);

        let iY = 0,
          iPY = 0,
          eY = 0,
          ePY = 0;
        let iM = 0,
          iPM = 0,
          eM = 0,
          ePM = 0;
        let iW = 0,
          iPW = 0,
          eW = 0,
          ePW = 0;

        const incomeMonthly = Array(12).fill(0);
        const expenseMonthly = Array(12).fill(0);
        const incomeTotals = incomeCategories.reduce((o, cat) => ({ ...o, [cat]: 0 }), {});
        const expenseTotals = expenseCategories.reduce((o, cat) => ({ ...o, [cat]: 0 }), {});

        txns.forEach((t) => {
          const d = new Date(t.date);
          const amt = parseFloat(t.amount);
          const y = d.getFullYear();
          const m = d.getMonth();
          const isIncome = t.type === "income";

          if (y === thisYear) {
            if (isIncome) {
              iY += amt;
              incomeMonthly[m] += amt;
              if (incomeTotals[t.category] != null) incomeTotals[t.category] += amt;
            } else {
              eY += amt;
              expenseMonthly[m] += amt;
              if (expenseTotals[t.category] != null) expenseTotals[t.category] += amt;
            }
          }

          if (y === thisYear - 1) {
            if (isIncome) iPY += amt;
            else ePY += amt;
          }

          if (y === thisYear) {
            if (d >= startOfMonth && d < startOfNextMonth) {
              if (isIncome) iM += amt;
              else eM += amt;
            }
            if (d >= startOfLastMonth && d < startOfMonth) {
              if (isIncome) iPM += amt;
              else ePM += amt;
            }
          }

          if (y === thisYear) {
            if (d >= startOfThisWeek && d < startOfNextWeek) {
              if (isIncome) iW += amt;
              else eW += amt;
            }
            if (d >= startOfLastWeek && d < startOfThisWeek) {
              if (isIncome) iPW += amt;
              else ePW += amt;
            }
          }
        });

        setIncomeYear(iY);
        setPrevIncomeYear(iPY);
        setExpenseYear(eY);
        setPrevExpenseYear(ePY);
        setIncomeMonth(iM);
        setPrevIncomeMonth(iPM);
        setExpenseMonth(eM);
        setPrevExpenseMonth(ePM);
        setIncomeWeek(iW);
        setPrevIncomeWeek(iPW);
        setExpenseWeek(eW);
        setPrevExpenseWeek(ePW);

        const months = [
          "Jan","Feb","Mar","Apr","May","Jun",
          "Jul","Aug","Sep","Oct","Nov","Dec"
        ];
        setMonthlyData(
          months.map((m, idx) => ({
            month: m,
            income: incomeMonthly[idx],
            expense: expenseMonthly[idx],
          }))
        );

        setIncomeRadar(
          incomeCategories.map((cat) => ({ subject: cat, A: incomeTotals[cat] }))
        );
        setExpenseRadar(
          expenseCategories.map((cat) => ({ subject: cat, A: expenseTotals[cat] }))
        );
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (a) => `€${a.toFixed(2)}`;
  const getChange = (cur, prev) => {
    if (prev === 0) {
      if (cur === 0) {
        return { percent: 0, direction: "up" }; 
      }
      return { percent: 100, direction: "up" }; 
    }

    let pct = ((cur - prev) / prev) * 100;

    if (pct > 999) pct = 999;
    if (pct < -999) pct = -999;

    return { percent: Math.abs(pct).toFixed(1), direction: pct >= 0 ? "up" : "down" };
  };




  const yearIncChange = getChange(incomeYear, prevIncomeYear);
  const yearExpChange = getChange(expenseYear, prevExpenseYear);
  const monthIncChange = getChange(incomeMonth, prevIncomeMonth);
  const monthExpChange = getChange(expenseMonth, prevExpenseMonth);
  const weekIncChange = getChange(incomeWeek, prevIncomeWeek);
  const weekExpChange = getChange(expenseWeek, prevExpenseWeek);

  const summary = [
    {
      label: "This Year",
      income: incomeYear,
      incCh: yearIncChange,
      expense: expenseYear,
      expCh: yearExpChange,
    },
    {
      label: "This Month",
      income: incomeMonth,
      incCh: monthIncChange,
      expense: expenseMonth,
      expCh: monthExpChange,
    },
    {
      label: "This Week",
      income: incomeWeek,
      incCh: weekIncChange,
      expense: expenseWeek,
      expCh: weekExpChange,
    },
  ];

  const AnimatedArrow = ({ direction }) => {
    const Icon = direction === "up" ? ArrowUpRight : ArrowDownRight;
    return (
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`flex items-center gap-1 text-sm ${
          direction === "up" ? "text-green-500" : "text-red-500"
        }`}
      >
        <Icon size={16} />
      </motion.div>
    );
  };

  // --- unchanged helper methods from your code ---
  const extractLabelAndPayload = (arg) => {
    if (!arg) return { label: null, payload: null };

    if (arg.activeLabel) {
      return {
        label: arg.activeLabel,
        payload: arg.activePayload || null,
      };
    }
    if (arg.payload && arg.payload.month) {
      return {
        label: arg.payload.month,
        payload: Array.isArray(arg) ? arg : [{ payload: arg.payload, value: arg.value }],
      };
    }
    if (arg.month) {
      return { label: arg.month, payload: [{ payload: arg, value: arg.income ?? arg.expense ?? 0 }] };
    }
    if (Array.isArray(arg) && arg[0] && arg[0].payload && arg[0].payload.month) {
      return { label: arg[0].payload.month, payload: arg };
    }
    if (arg.activePayload && Array.isArray(arg.activePayload) && arg.activePayload[0]) {
      const p = arg.activePayload[0].payload;
      return { label: p && p.month ? p.month : null, payload: arg.activePayload };
    }
    return { label: null, payload: null };
  };

  const toggleMobileTooltip = (arg) => {
    if (!isMobile) return;
    const { label, payload } = extractLabelAndPayload(arg);
    if (!label) return;
    if (activeTooltip && activeTooltip.label === label) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip({ label, payload });
    }
  };



  return (
    <div className="p-6 bg-gray-50 min-h-screen">
<div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
  {summary.map(({ label, income, incCh, expense, expCh }, i) => (
    <motion.div
      key={label}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * (i + 1) }}
      className="p-6 rounded-xl shadow bg-white"
    >
      <h3 className="text-gray-500 text-sm mb-4 text-center">
        Total Income & Expense {label}
      </h3>
      <div className="flex">
        {/* Income Block */}
        <div className="flex-1 text-center">
          <div className="flex items-center justify-center gap-1">
            <AnimatedArrow direction={incCh.direction} />
            <span className="text-green-500 font-semibold text-lg">
              {formatCurrency(income)}
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-1">Income</div>
          <div
            className={`text-xs mt-1 ${
              incCh.direction === "up" ? "text-green-500" : "text-red-500"
            }`}
          >
            {incCh.direction === "up" ? "+" : "-"}
            {incCh.percent}% vs last {label.split(" ")[1].toLowerCase()}
          </div>
        </div>

        <div className="w-px bg-gray-300 mx-2"></div>

        {/* Expense Block */}
        <div className="flex-1 text-center">
          <div className="flex items-center justify-center gap-1">
            <AnimatedArrow direction={expCh.direction} />
            <span className="text-red-500 font-semibold text-lg">
              {formatCurrency(expense)}
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-1">Expense</div>
          <div
            className={`text-xs mt-1 ${
              expCh.direction === "up" ? "text-green-500" : "text-red-500"
            }`}
          >
            {expCh.direction === "up" ? "+" : "-"}
            {expCh.percent}% vs last {label.split(" ")[1].toLowerCase()}
          </div>
        </div>
      </div>
    </motion.div>
  ))}
</div>


      {/* Row 1: Line + Income Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {/* Line chart */}
        <div className="col-span-2 bg-white p-6 rounded-xl shadow">
          <h2 className="text-lg font-semibold mb-4">Income vs Expense</h2>

          {/* Mobile: scrollable */}
          <div className="block md:hidden overflow-x-auto">
            <div className="w-[700px] h-[300px]">
              <LineChart
                data={monthlyData}
                width={700}
                height={300}
                // chart-level click fallback (handles taps on axis/empty area that still provide activeLabel)
                onClick={(e) => toggleMobileTooltip(e)}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                {/* controlled tooltip on mobile, uncontrolled on desktop */}
                {isMobile ? (
                  <Tooltip
                    active={!!activeTooltip}
                    payload={activeTooltip ? activeTooltip.payload : []}
                    label={activeTooltip ? activeTooltip.label : ""}
                    formatter={(v) => `€${v.toFixed(2)}`}
                  />
                ) : (
                  <Tooltip formatter={(v) => `€${v.toFixed(2)}`} />
                )}
                <Line
                  type="monotone"
                  dataKey="income"
                  stroke="#2ecfe3"
                  strokeWidth={2}
                  name="Income"
                  // clicking the dot provides the payload directly, so also attach click
                  dot={{
                    r: 4,
                    onClick: (d) => toggleMobileTooltip(d && d.payload ? d.payload : d),
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="expense"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Expense"
                  dot={{
                    r: 4,
                    onClick: (d) => toggleMobileTooltip(d && d.payload ? d.payload : d),
                  }}
                />
              </LineChart>
            </div>
          </div>

          {/* Desktop */}
          <div className="hidden md:block">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(v) => `€${v.toFixed(2)}`} />
                <Line
                  type="monotone"
                  dataKey="income"
                  stroke="#2ecfe3"
                  strokeWidth={2}
                  name="Income"
                />
                <Line
                  type="monotone"
                  dataKey="expense"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Expense"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Income Radar */}
        <div className="bg-white p-6 rounded-xl shadow flex justify-center">
          <div className="w-full max-w-xs">
            <h2 className="text-lg font-semibold mb-4 text-center">
              Income Sources
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart
                cx="50%"
                cy="50%"
                outerRadius="80%"
                data={incomeRadarData}
              >
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis
                  angle={30}
                  domain={[
                    0,
                    Math.max(...incomeRadarData.map((d) => d.A)) || 100,
                  ]}
                />
                <Radar
                  name="Income"
                  dataKey="A"
                  stroke="#2E2E2E"
                  fill="#2ecfe3"
                  fillOpacity={0.6}
                />
                {/* mobile: click trigger to avoid tooltip overlap; desktop default */}
                {isMobile ? (
                  <Tooltip
                    trigger="click"
                    formatter={(v) => `€${v.toFixed(2)}`}
                  />
                ) : (
                  <Tooltip formatter={(v) => `€${v.toFixed(2)}`} />
                )}
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 2: Bar + Expense Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar chart */}
        <div className="col-span-2 bg-white p-6 rounded-xl shadow">
          <h2 className="text-lg font-semibold mb-4">Monthly Comparison</h2>

          {/* Mobile: scrollable */}
          <div className="block md:hidden overflow-x-auto">
            <div className="w-[700px] h-[300px]">
              <BarChart
                data={monthlyData}
                width={700}
                height={300}
                onClick={(e) => toggleMobileTooltip(e)}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                {isMobile ? (
                  <Tooltip
                    active={!!activeTooltip}
                    payload={activeTooltip ? activeTooltip.payload : []}
                    label={activeTooltip ? activeTooltip.label : ""}
                    formatter={(v) => `€${v.toFixed(2)}`}
                  />
                ) : (
                  <Tooltip formatter={(v) => `€${v.toFixed(2)}`} />
                )}
                <Bar
                  dataKey="income"
                  fill="#2ecfe3"
                  name="Income"
                  onClick={(d) => toggleMobileTooltip(d)}
                />
                <Bar
                  dataKey="expense"
                  fill="#ef4444"
                  name="Expense"
                  onClick={(d) => toggleMobileTooltip(d)}
                />
              </BarChart>
            </div>
          </div>

          {/* Desktop */}
          <div className="hidden md:block">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(v) => `€${v.toFixed(2)}`} />
                <Bar dataKey="income" fill="#2ecfe3" name="Income" />
                <Bar dataKey="expense" fill="#ef4444" name="Expense" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Expense Radar */}
        <div className="bg-white p-6 rounded-xl shadow flex justify-center">
          <div className="w-full max-w-xs">
            <h2 className="text-lg font-semibold mb-4 text-center">
              Expense Sources
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart
                cx="50%"
                cy="50%"
                outerRadius="80%"
                data={expenseRadarData}
              >
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis
                  angle={30}
                  domain={[
                    0,
                    Math.max(...expenseRadarData.map((d) => d.A)) || 100,
                  ]}
                />
                <Radar
                  name="Expense"
                  dataKey="A"
                  stroke="#2E2E2E"
                  fill="#ef4444"
                  fillOpacity={0.6}
                />
                {isMobile ? (
                  <Tooltip
                    trigger="click"
                    formatter={(v) => `€${v.toFixed(2)}`}
                  />
                ) : (
                  <Tooltip formatter={(v) => `€${v.toFixed(2)}`} />
                )}
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}